import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NumberField } from "@/components/calc/NumberField";
import { Cpu, MapPin, Plus, Trash2, Save, Printer, Clock, Info, Package } from "lucide-react";
import { toast } from "sonner";
import { useAppSettings } from "@/context/AppSettings";
import { PRESET_DEVICES, fmtCurrency, CURRENCIES, DEFAULT_PARAMS } from "@/lib/calc";
import { fetchDevices, saveTreatment } from "@/lib/api";
import { api } from "@/lib/http";
import { TREATMENT_AREAS, COMMON_CONSUMABLES, calcTreatment } from "@/lib/business";

const CATEGORIES = [
  { key: "laser", label: "Laser", mode: "pulses" },
  { key: "ipl", label: "IPL", mode: "pulses" },
  { key: "led", label: "LED Therapy", mode: "session" },
  { key: "skin_imaging", label: "Skin Imaging", mode: "session" },
  { key: "hydrafacial", label: "Hydrafacial", mode: "session" },
  { key: "custom_device", label: "Custom Device", mode: "session" },
  { key: "pmu", label: "Permanent Makeup", mode: "none" },
  { key: "lashes", label: "Lashes", mode: "none" },
  { key: "injectables", label: "Injectables", mode: "none" },
  { key: "facial", label: "Facial", mode: "none" },
  { key: "waxing", label: "Waxing", mode: "none" },
  { key: "massage", label: "Massage", mode: "none" },
];

export default function TreatmentBuilder() {
  const { currency, business } = useAppSettings();
  const sym = (CURRENCIES[currency] || CURRENCIES.USD).symbol;

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [category, setCategory] = useState("laser");
  const [sessionCost, setSessionCost] = useState(0);
  const [products, setProducts] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [overheadPerHour, setOverheadPerHour] = useState(0);
  const [operatingHours, setOperatingHours] = useState(0);
  const [area, setArea] = useState(TREATMENT_AREAS[5].name);
  const [name, setName] = useState(TREATMENT_AREAS[5].name);
  const [pulses, setPulses] = useState(TREATMENT_AREAS[5].pulses);
  const [materials, setMaterials] = useState([
    { name: "Cooling gel", cost: 1.5 },
    { name: "Disposable eyewear", cost: 1.0 },
    { name: "Bed cover", cost: 1.5 },
    { name: "Gloves", cost: 0.6 },
  ]);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(20);
  const [margin, setMargin] = useState(60);
  const [priceMode, setPriceMode] = useState("margin"); // "margin" | "price"
  const [manualPrice, setManualPrice] = useState(""); // the round price the client pays (incl GST)
  const [saving, setSaving] = useState(false);

  const mode = useMemo(() => CATEGORIES.find((c) => c.key === category)?.mode || "none", [category]);

  useEffect(() => {
    fetchDevices().then((d) => { setDevices(d); if (d[0]) setDeviceId((id) => id || d[0].id); }).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/business").then(({ data }) => {
      const fc = data.fixed_costs || [];
      const per = { weekly: 52 / 12, monthly: 1, quarterly: 1 / 3, annual: 1 / 12 };
      const monthlyFixed = fc.reduce((s, r) => s + (parseFloat(r.amount) || 0) * (per[r.frequency] || 1), 0);
      const oh = data.operating_hours_per_month || 0;
      setOperatingHours(oh);
      setOverheadPerHour(oh > 0 ? monthlyFixed / oh : 0);
    }).catch(() => {});
  }, []);

  const device = useMemo(() => devices.find((d) => d.id === deviceId) || devices[0] || { id: "", name: "—", params: DEFAULT_PARAMS }, [devices, deviceId]);

  // product usage converted into material line costs (qty × price per unit)
  const productMaterials = useMemo(() => productLines.map((l) => {
    const p = products.find((x) => x.id === l.product_id);
    const qty = parseFloat(l.qty) || 0;
    return p ? { name: `${p.name} (${qty}${p.unit})`, cost: (p.price_per_unit || 0) * qty } : null;
  }).filter(Boolean), [productLines, products]);

  const effectivePulses = mode === "pulses" ? pulses : 0;
  const sessionMaterial = mode === "session" && (parseFloat(sessionCost) || 0) > 0
    ? [{ name: "Device / session cost", cost: parseFloat(sessionCost) || 0 }] : [];
  const allMaterials = useMemo(() => [...materials, ...sessionMaterial, ...productMaterials], [materials, sessionMaterial, productMaterials]);

  const addProductLine = () => setProductLines((l) => [...l, { product_id: products[0]?.id || "", qty: 1 }]);
  const updateProductLine = (i, k, v) => setProductLines((l) => l.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeProductLine = (i) => setProductLines((l) => l.filter((_, idx) => idx !== i));

  const [customAreas, setCustomAreas] = useState([]);
  const [newAreaName, setNewAreaName] = useState("");
  const [newAreaPulses, setNewAreaPulses] = useState(300);
  const allAreas = useMemo(() => [...TREATMENT_AREAS, ...customAreas], [customAreas]);

  const onArea = (a) => {
    setArea(a);
    const preset = allAreas.find((t) => t.name === a);
    if (preset) { setPulses(preset.pulses); setName(a); }
  };

  const addCustomArea = () => {
    const nm = newAreaName.trim();
    if (!nm) { toast.error("Enter an area name"); return; }
    const p = parseFloat(newAreaPulses) || 0;
    setCustomAreas((list) => [...list, { name: nm, pulses: p }]);
    setArea(nm); setName(nm); setPulses(p);
    setNewAreaName(""); setNewAreaPulses(300);
    toast.success(`Added "${nm}"`);
  };

  const addMaterial = (m) => setMaterials((list) => [...list, m || { name: "", cost: 0 }]);
  const updateMaterial = (i, key, val) => setMaterials((list) => list.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const removeMaterial = (i) => setMaterials((list) => list.filter((_, idx) => idx !== i));

  const gstRateNum = business.gstEnabled ? (parseFloat(business.gstRate) || 0) : 0;
  const manualExGst = priceMode === "price" && manualPrice !== "" && !isNaN(parseFloat(manualPrice))
    ? (parseFloat(manualPrice) || 0) / (1 + gstRateNum / 100)
    : undefined;

  const r = useMemo(
    () => calcTreatment({ deviceParams: device.params, pulses: effectivePulses, materials: allMaterials, timeHours: hours, timeMinutes: minutes, margin, priceExGstOverride: manualExGst, business: { ...business, overheadPerHour, operatingHoursPerMonth: operatingHours } }),
    [device, effectivePulses, allMaterials, hours, minutes, margin, manualExGst, business, overheadPerHour, operatingHours]
  );

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Enter a treatment name"); return; }
    setSaving(true);
    try {
      await saveTreatment({
        name: name.trim(), area, category, session_cost: parseFloat(sessionCost) || 0,
        device_id: device.id, device_name: device.name,
        pulses: parseFloat(effectivePulses) || 0, materials, products: productLines,
        time_hours: parseFloat(hours) || 0,
        time_minutes: parseFloat(minutes) || 0,
        margin: priceMode === "price" ? Math.round(r.marginPct * 10) / 10 : margin,
        result: {
          laserCost: r.laserCost, materialsCost: r.materialsCost, timeCost: r.timeCost,
          overheadCost: r.overheadCost, reserveCost: r.reserveCost,
          breakEven: r.breakEven, priceExGst: r.priceExGst, gstAmount: r.gstAmount,
          priceInclGst: r.priceInclGst, currency, gstEnabled: business.gstEnabled,
        },
      });
      toast.success(`Saved "${name}" to your Price List`);
    } catch (e) {
      toast.error("Couldn't save treatment");
    } finally {
      setSaving(false);
    }
  };

  const gstLabel = business.gstEnabled ? "Price incl. GST" : "Price";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Treatment Builder</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Price any treatment — laser, LED, facials, injectables, PMU, lashes, waxing, massage and more — using
            true device/running costs, products from your inventory, staff time and consumables.
          </p>
        </div>
        <Link to="/business-setup" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="link-business-setup">
          <Info className="h-3.5 w-3.5" /> Uses {business.country} staff & tax settings
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Left: inputs */}
        <div className="space-y-4 min-w-0">
          <Card className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Cpu className="h-4 w-4 text-primary" />
              <h3 className="font-heading font-semibold text-base">Treatment type</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="treatment-category-selector"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`category-${c.key}`}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Treatment name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-lg" data-testid="input-treatment-name" />
              </div>
            </div>

            {mode === "pulses" && (
              <>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Laser device</label>
                    <Select value={deviceId} onValueChange={setDeviceId}>
                      <SelectTrigger className="h-11 rounded-lg" data-testid="treatment-device-selector"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {devices.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`treatment-device-${d.id}`}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Treatment area</label>
                    <Select value={area} onValueChange={onArea}>
                      <SelectTrigger className="h-11 rounded-lg" data-testid="treatment-area-selector"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allAreas.map((t) => <SelectItem key={t.name} value={t.name} data-testid={`area-option-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 mt-2">
                      <Input value={newAreaName} onChange={(e) => setNewAreaName(e.target.value)} placeholder="Add custom area" className="h-9 rounded-lg text-sm" data-testid="new-area-name" />
                      <Input type="number" min="0" value={newAreaPulses} onChange={(e) => setNewAreaPulses(e.target.value)} className="h-9 rounded-lg w-24 text-sm tabular-nums" placeholder="pulses" data-testid="new-area-pulses" />
                      <Button type="button" variant="outline" size="sm" className="h-9 rounded-lg shrink-0" onClick={addCustomArea} data-testid="add-area-btn"><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <NumberField label="Pulses (this area)" unit="pulses" value={pulses} onChange={setPulses} testId="input-treatment-pulses" />
                </div>
                <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  Laser running cost: <span className="font-medium text-foreground tabular-nums">{fmtCurrency(r.laserCost, currency)}</span>
                  <span className="text-muted-foreground/70">({r.perPulse ? `${sym}${r.perPulse.toFixed(5)}/pulse` : ""})</span>
                </div>
              </>
            )}

            {mode === "session" && (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5">
                  <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Device / session cost</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                    <Input type="number" min="0" value={sessionCost} onChange={(e) => setSessionCost(e.target.value)} className="h-11 rounded-lg pl-7 tabular-nums" data-testid="treatment-session-cost" />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Per-treatment running/consumable cost of the device (e.g. serums, tips, cartridges).</p>
                </div>
              </div>
            )}
          </Card>

          {/* Products from inventory */}
          <Card className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                <h3 className="font-heading font-semibold text-base">Products used</h3>
              </div>
              <span className="font-heading font-bold text-primary tabular-nums" data-testid="products-subtotal">{fmtCurrency(productMaterials.reduce((a, m) => a + m.cost, 0), currency)}</span>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">No products yet. Add stock in <Link to="/inventory" className="text-primary hover:underline">Inventory</Link> to assign them here.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {productLines.map((l, i) => {
                    const p = products.find((x) => x.id === l.product_id);
                    return (
                      <div key={i} className="flex items-center gap-2" data-testid={`product-line-${i}`}>
                        <Select value={l.product_id} onValueChange={(v) => updateProductLine(i, "product_id", v)}>
                          <SelectTrigger className="h-10 rounded-lg flex-1" data-testid={`product-line-select-${i}`}><SelectValue placeholder="Product" /></SelectTrigger>
                          <SelectContent>{products.map((pp) => <SelectItem key={pp.id} value={pp.id}>{pp.name} ({fmtCurrency(pp.price_per_unit, currency, 4)}/{pp.unit})</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min="0" value={l.qty} onChange={(e) => updateProductLine(i, "qty", e.target.value)} className="h-10 rounded-lg w-24 tabular-nums" data-testid={`product-line-qty-${i}`} placeholder="qty" />
                        <span className="text-xs text-muted-foreground w-8">{p?.unit}</span>
                        <button onClick={() => removeProductLine(i)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive shrink-0" data-testid={`product-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={addProductLine} className="mt-3 text-sm text-primary flex items-center gap-1 hover:underline" data-testid="add-product-line"><Plus className="h-4 w-4" /> Assign product</button>
              </>
            )}
          </Card>

          {/* Materials */}
          <Card className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-secondary" />
                <h3 className="font-heading font-semibold text-base">Materials & Consumables</h3>
              </div>
              <span className="font-heading font-bold text-primary tabular-nums" data-testid="materials-subtotal">{fmtCurrency(materials.reduce((a, m) => a + (parseFloat(m.cost) || 0), 0), currency)}</span>
            </div>
            <div className="space-y-2">
              {materials.map((m, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`material-row-${i}`}>
                  <Input value={m.name} onChange={(e) => updateMaterial(i, "name", e.target.value)} placeholder="Item name" className="h-10 rounded-lg flex-1" data-testid={`material-name-${i}`} />
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                    <Input type="number" min="0" value={m.cost} onChange={(e) => updateMaterial(i, "cost", e.target.value)} className="h-10 rounded-lg pl-7 tabular-nums" data-testid={`material-cost-${i}`} />
                  </div>
                  <button onClick={() => removeMaterial(i)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive shrink-0" data-testid={`material-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => addMaterial()} className="mt-3 text-sm text-primary flex items-center gap-1 hover:underline" data-testid="add-material-button">
              <Plus className="h-4 w-4" /> Add custom item
            </button>
            <Separator className="my-3" />
            <div className="flex flex-wrap gap-2">
              {COMMON_CONSUMABLES.map((c) => (
                <Button key={c.name} variant="outline" size="sm" className="h-7 rounded-full text-xs" onClick={() => addMaterial({ ...c })} data-testid={`consumable-chip-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <Plus className="h-3 w-3 mr-1" />{c.name}
                </Button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right: time + margin */}
        <div className="space-y-4 min-w-0">
          <Card className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-accent" />
              <h3 className="font-heading font-semibold text-base">Treatment Time</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Hours" value={hours} onChange={setHours} testId="input-treatment-hours" />
              <NumberField label="Minutes" value={minutes} onChange={setMinutes} testId="input-treatment-minutes" />
            </div>
            <div className="mt-3 text-xs text-muted-foreground flex items-center justify-between">
              <span>Staff cost @ {fmtCurrency(r.hourly, currency)}/hr (true cost)</span>
              <span className="font-medium text-foreground tabular-nums">{fmtCurrency(r.timeCost, currency)}</span>
            </div>
          </Card>

          <Card className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Pricing</span>
              <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
                <button type="button" onClick={() => setPriceMode("margin")} data-testid="price-mode-margin"
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${priceMode === "margin" ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"}`}>
                  By margin %
                </button>
                <button type="button" onClick={() => setPriceMode("price")} data-testid="price-mode-price"
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${priceMode === "price" ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"}`}>
                  Set price
                </button>
              </div>
            </div>

            {priceMode === "margin" ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Desired profit margin</span>
                  <span className="font-heading font-bold text-lg tabular-nums" data-testid="treatment-margin-value">{margin}%</span>
                </div>
                <Slider value={[margin]} min={20} max={90} step={1} onValueChange={(v) => setMargin(v[0])} data-testid="input-treatment-margin" />
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm text-muted-foreground">{gstLabel} you charge the client</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground">{sym}</span>
                    <Input type="number" min="0" step="1" inputMode="decimal" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)}
                      placeholder="e.g. 350" className="h-12 rounded-lg pl-7 tabular-nums text-lg font-heading font-bold" data-testid="input-treatment-price" />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 rounded-lg bg-muted/40 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Resulting profit margin</span>
                  <span className={`font-heading font-bold text-lg tabular-nums ${r.marginPct < 0 ? "text-destructive" : "text-primary"}`} data-testid="treatment-derived-margin">{r.marginPct.toFixed(1)}%</span>
                </div>
                {r.marginPct < 0 && <p className="text-[11px] text-destructive mt-2">This price is below your break-even — you'd make a loss.</p>}
              </>
            )}
            <Separator className="my-4" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Machine, servicing &amp; electricity</span><span className="tabular-nums">{fmtCurrency(r.laserCost, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Materials</span><span className="tabular-nums">{fmtCurrency(r.materialsCost, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Staff time</span><span className="tabular-nums">{fmtCurrency(r.timeCost, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Major failure reserve</span><span className="tabular-nums" data-testid="summary-reserve">{fmtCurrency(r.reserveCost, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Overhead (fixed costs)</span><span className="tabular-nums" data-testid="summary-overhead">{fmtCurrency(r.overheadCost, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Profit</span><span className="tabular-nums text-primary">{fmtCurrency(r.profit, currency)}</span></div>
              {business.gstEnabled && (
                <div className="flex justify-between"><span className="text-muted-foreground">GST ({business.gstRate}%)</span><span className="tabular-nums">{fmtCurrency(r.gstAmount, currency)}</span></div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Summary bar */}
      <Card className="rounded-2xl border-0 bg-gradient-to-r from-primary to-secondary text-primary-foreground p-6 shadow-lg mt-6" data-testid="treatment-summary">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="font-mono-label text-[11px] uppercase tracking-wider opacity-80">Materials</div>
            <div className="font-heading font-extrabold text-2xl tabular-nums mt-1" data-testid="summary-materials">{fmtCurrency(r.materialsCost, currency)}</div>
          </div>
          <div>
            <div className="font-mono-label text-[11px] uppercase tracking-wider opacity-80">Time Cost · {r.timeHrs.toFixed(2)} hrs</div>
            <div className="font-heading font-extrabold text-2xl tabular-nums mt-1" data-testid="summary-time-cost">{fmtCurrency(r.timeCost, currency)}</div>
          </div>
          <div>
            <div className="font-mono-label text-[11px] uppercase tracking-wider opacity-80">Break-Even</div>
            <div className="font-heading font-extrabold text-2xl tabular-nums mt-1" data-testid="summary-break-even">{fmtCurrency(r.breakEven, currency)}</div>
          </div>
          <div>
            <div className="font-mono-label text-[11px] uppercase tracking-wider opacity-80">{gstLabel}</div>
            <div className="font-heading font-extrabold text-2xl tabular-nums mt-1" data-testid="summary-price">{fmtCurrency(r.priceInclGst, currency)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          <Button onClick={handleSave} disabled={saving} className="rounded-lg bg-primary-foreground text-primary hover:bg-primary-foreground/90" data-testid="save-treatment-button">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Treatment"}
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="rounded-lg bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10" data-testid="print-treatment-button">
            <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}
